import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog";
import Button from "../../form/button";
import { useState } from "react";
import { useToast } from "../../ui/use-toast";
import { IDailyRecord, Status } from "../../../lib/types";
import { _Classes } from "../../../lib/styles";
import { ToastAction } from "@radix-ui/react-toast";

import React from "react";

import ErrorMessage from "../../form/errorMessage";
import pb from "../../../lib/pocketbase";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { DatePicker } from "../../ui/datepicker";
import { fullDate, toDate } from "../../../lib/utils";

interface EditDailyModalProps {
    todo: IDailyRecord;
    todos: IDailyRecord[];
    setTodos: React.Dispatch<React.SetStateAction<IDailyRecord[]>>;
}

export function EditDailyModal({ todo, todos, setTodos }: EditDailyModalProps) {
    const [open, setOpen] = useState(false);
    const { toast } = useToast();
    const [text, setText] = useState(todo.text);
    const [date, setDate] = useState(toDate(todo.date));
    const [description, setDescription] = useState(todo.description);
    const [savingStatus, setSavingStatus] = useState<Status>("none");
    const [error, setError] = useState<string>("");

    async function save() {
        setSavingStatus("loading");
        const oldTodo = todo;
        todo.text = text;
        todo.description = description;
        todo.date = fullDate(date);
        //todo.date = fullDate(date);
        updateValue(todo);
        updateTodo(todo)
            .then(() => {
                setTimeout(() => {
                    setSavingStatus("success");
                    setError("");
                    setOpen(false);
                    toast({
                        title: `👍 Done!`,
                        description: `I have updated '${oldTodo.text}'`,
                        action: (
                            <ToastAction altText="Undo">
                                <Button variant={"outline"} onClick={() => undo(oldTodo)} size={"extraSmall"}>
                                    Undo
                                </Button>
                            </ToastAction>
                        )
                    });
                }, 500);
            })
            .catch((err: Error) => {
                setSavingStatus("error");

                setError(err.message);
            })
            .finally(() => {
                setTimeout(() => {
                    setSavingStatus("none");
                }, 3000);
            });
    }

    async function updateTodo(todo: IDailyRecord) {
        return pb.collection("daily").update(todo.id, {
            text: text,
            description: description,
            date: fullDate(date)
        });
    }

    function updateValue(todo: IDailyRecord) {
        let newTodos = [...todos];
        for (let i = 0; i < newTodos.length; i++) {
            if (newTodos[i].id === todo.id) {
                newTodos[i] = todo;
                break; // Remove this line if you want to change all matching objects
            }
        }
        setTodos(newTodos);
    }
    function undo(todo: IDailyRecord) {
        updateTodo(todo);
        updateValue(todo);
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="ghost" variant="ghost" className="group">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-5 w-5 text-slate-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-5 group-hover:text-slate-700  dark:text-slate-600 dark:ring-offset-slate-900 focus-visible:dark:ring-slate-700 dark:group-hover:text-slate-300">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                    </svg>
                </Button>
            </DialogTrigger>
            <DialogContent className="mt-8 md:mt-0">
                <DialogHeader>
                    <DialogTitle>Edit daily item</DialogTitle>
                    <DialogDescription>Edit '{todo.text}', click Save to save.</DialogDescription>
                </DialogHeader>
                <ErrorMessage error={error} close={() => setError("")} />

                <Input
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            save();
                        }
                    }}
                    defaultValue={todo.text}
                    onChange={(e) => setText(e.target.value)}
                    type="text"
                    placeholder="What are you getting done?"
                    autoFocus></Input>
                <DatePicker date={date} setDate={(d: Date) => setDate(d)} />
                <Textarea className="h-48" defaultValue={todo.description} onKeyDown={(e) => e.key === "Enter" && e.shiftKey && save()} placeholder="Anything to add?" onChange={(e) => setDescription(e.target.value)}></Textarea>
                <DialogFooter className="gap-2">
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button status={savingStatus} onClick={() => save()} variant="dark">
                        {savingStatus === "loading" && "Saving"}
                        {savingStatus === "success" && "Saved"}
                        {savingStatus === "error" && "Error"}
                        {savingStatus === "none" && "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
